import { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useRouter } from 'next/router'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import styled from '@emotion/styled'
import Snackbar from '@mui/material/Snackbar'
import { generateShortUrls } from 'utils/api'
import { LS_APP_DOMAIN, FB_APP_ID } from 'config/constants'

import { selectWebview } from 'store/webview/webviewSlice'
import { shareUrlFromNative, copyTextFromNative } from '/utils/native-notifier'
import { copyToClipboard } from 'utils/helpers'
import { FacebookIconSVG, LineCircleSVG } from 'components/common/SVGElements'
import { HideInDesktopBox, HideInMobileGrid } from 'components/common/Hidden'

import { setToast } from 'store/toast/toastSlice'

const HoverAbsolute = styled.div`
  ${({ theme, isColumn }) => `
    color: ${theme.palette.text.secondary};
    position: absolute;
    top: ${isColumn ? 0 : '-4px'};
    left: ${isColumn ? '54px' : '50%'};
    transform: ${isColumn ? 'translate(0%, 13%)' : 'translate(-50%, -100%)'};
    background-color: rgba(256, 256, 256, 0.9);
    width: auto;
    white-space: nowrap;
    border-radius: 6px;
    padding: 6px;
    z-index: 4;
  `}
`

const genLink = (route, URL_UTM) => {
  return LS_APP_DOMAIN + route + URL_UTM
}

const genShortLink = async (route, URL_UTM) => {
  const link = genLink(route, URL_UTM)
  const [shortLinks, error] = await generateShortUrls({ urls: [link] })
  return [shortLinks[link], error]
}

const ShareIcon = ({ iconComponent, onClick, isColumn, toastContent }) => {
  const [isHover, setIsHover] = useState(false)
  return (
    <Box
      onClick={onClick}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      sx={{ position: 'relative', cursor: 'pointer' }}
      backgroundColor={isHover ? 'primary.bgPlus1' : 'grey.third'}
      px={4}
      py={2.5}
      borderRadius="30px"
    >
      {isHover && (
        <HoverAbsolute isColumn={isColumn}>
          <Typography>{toastContent}</Typography>
        </HoverAbsolute>
      )}
      {iconComponent(isHover)}
    </Box>
  )
}

const ShareButton = ({ shareText, isColumn, isCenter, utmSource }) => {
  const router = useRouter()
  const theme = useTheme()
  const [height, setHeight] = useState(0)
  const webview = useSelector(selectWebview)

  const FB_UTM = `?utm_source=${utmSource}&utm_medium=share&utm_campaign=FB`
  const LINE_UTM = `?utm_source=${utmSource}&utm_medium=share&utm_campaign=LINE`
  const SHARE_UTM = `?utm_source=${utmSource}&utm_medium=share&utm_campaign=othershare`
  const COPY_UTM = `?utm_source=${utmSource}&utm_medium=share&utm_campaign=copylink`

  const dispatch = useDispatch()

  // 適應resize尺寸高度
  useEffect(() => {
    const updateWindowInnerHeight = () => {
      const windowInnerHeight = window.innerHeight - 100
      setHeight(windowInnerHeight)
    }

    window.addEventListener('resize', updateWindowInnerHeight)
    updateWindowInnerHeight()
    return () => window.removeEventListener('resize', updateWindowInnerHeight)
  }, [])

  const onFBClick = async () => {
    if (!router) {
      return
    }
    const [link, error] = await genShortLink(
      router.asPath.split('?')?.[0],
      FB_UTM
    )

    if (!error) {
      window.open(
        `https://www.facebook.com/dialog/share?app_id=${FB_APP_ID}` +
          `&href=${encodeURIComponent(link)}` +
          `&redirect_uri=${encodeURIComponent(location.href)}` +
          '&display=popup',
        '_blank'
      )
    }
  }

  const onLineClick = async () => {
    if (!router) {
      return
    }
    const [link, error] = await genShortLink(
      router.asPath.split('?')?.[0],
      LINE_UTM
    )
    if (!error) {
      window.open(
        'https://social-plugins.line.me/lineit/share?url=' +
          encodeURIComponent(link),
        '_blank'
      )
    }
  }

  const onShareClick = async () => {
    const [link, error] = await genShortLink(
      router.asPath.split('?')?.[0],
      SHARE_UTM
    )

    if (!error) {
      if (webview?.name === 'lsApp') {
        shareUrlFromNative(shareText, link)
      } else {
        const shareData = { title: '信義居家', text: shareText, url: link }
        try {
          await navigator.share(shareData)
          dispatch(setToast({ open: true, message: '分享成功' }))
        } catch (err) {
          console.log(err)
        }
      }
    }
  }

  const onCloneLinkClick = async () => {
    const [link, error] = await genShortLink(
      router.asPath.split('?')?.[0],
      COPY_UTM
    )

    if (!error) {
      if (webview?.name === 'lsApp') {
        copyTextFromNative(link)
        dispatch(setToast({ open: true, message: '複製完成' }))
      } else {
        copyToClipboard(link)
        dispatch(setToast({ open: true, message: '複製完成' }))
      }
    }
  }

  return (
    <Box sx={{ height: isColumn && height }}>
      <Grid
        direction={isColumn ? 'column' : 'row'}
        sx={{ height: isColumn && height }}
        justifyContent={{ lg: isCenter ? 'center' : '', xl: 'center' }}
        alignItems="center"
        container
        columnSpacing={isColumn ? 0 : 3}
        rowSpacing={isColumn ? 3 : 0}
      >
        <Grid item>
          <ShareIcon
            isColumn={isColumn}
            toastContent="更多分享"
            iconComponent={(isHover) => (
              <Grid container alignItems="center">
                <img
                  width={20}
                  height={20}
                  alt=""
                  src="/img/share.svg"
                  style={{ display: 'block' }}
                />
                <HideInDesktopBox ml={1.5}>分享</HideInDesktopBox>
              </Grid>
            )}
            onClick={onShareClick}
          />
        </Grid>
        <HideInMobileGrid item>
          <ShareIcon
            isColumn={isColumn}
            toastContent="複製網址"
            iconComponent={(isHover) => (
              <img
                width={20}
                height={20}
                alt=""
                src="/img/copy.svg"
                style={{ display: 'block' }}
              />
            )}
            onClick={onCloneLinkClick}
          />
        </HideInMobileGrid>
        <HideInMobileGrid item>
          <ShareIcon
            isColumn={isColumn}
            toastContent="分享至FB"
            iconComponent={(isHover) => (
              <FacebookIconSVG
                theme={theme}
                $ptr
                $color={'blue.main'}
                width="20px"
                height="20px"
                display="block"
              />
            )}
            onClick={onFBClick}
          />
        </HideInMobileGrid>
        <HideInMobileGrid item>
          <ShareIcon
            isColumn={isColumn}
            toastContent="分享至LINE"
            iconComponent={(isHover) => (
              <LineCircleSVG
                theme={theme}
                $ptr
                width="20px"
                height="20px"
                display="block"
              />
            )}
            onClick={onLineClick}
          />
        </HideInMobileGrid>
      </Grid>
    </Box>
  )
}

export default ShareButton
